import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { BASE_LMSA_URL } from '../../app.constants';
import { AuthService } from '../service/auth.service';
import { finalize, map, tap, filter, catchError } from 'rxjs/operators';
import { Observable, from } from 'rxjs';
import { StorageMap } from '@ngx-pwa/local-storage';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    constructor(private authService: AuthService, public storage: StorageMap) { }

    lastAuthenticated: any = null;
    private refreshPromise: Promise<void> | null = null;

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (request.url){
            request = request.clone({ url: request.url.trim() });
        }
        if (!request || !request.url || (/^http/.test(request.url) && !(BASE_LMSA_URL && request.url.startsWith(BASE_LMSA_URL)))) {
            return next.handle(request);
        }        

        // return next.handle(request);
        return from(this.getHeader(request, next));
    }

    private isNonceStale(): boolean {
        if (!this.lastAuthenticated) {
            return true;
        }
        const now: any = new Date();
        const mins = Math.abs(this.lastAuthenticated - now) / 60000;
        return mins >= 3;
    }

    private isAuthChallenge(error: any): boolean {
        return !!error && (error.status === 401 || error.status === 403);
    }

    private getWwwAuthenticate(error: any): string | null {
        if (!error || !error.headers || typeof error.headers.get !== 'function') {
            return null;
        }
        return error.headers.get('www-authenticate') || error.headers.get('WWW-Authenticate');
    }

    async refreshDigestAuth(requestUrl: string, options: any): Promise<void> {
        if (this.refreshPromise) {
            await this.refreshPromise;
        }
        if (!this.isNonceStale() && this.authService.userDigestAuth && this.authService.userDigestAuth.hasAuth) {
            return;
        }
        if (this.refreshPromise) {
            await this.refreshPromise;
            return;
        }

        this.refreshPromise = (async () => {
            try {
                await this.authService.checkForAuthAsync();
                const digestAuth = this.authService.userDigestAuth;
                if (!digestAuth) {
                    return;
                }
                digestAuth.hasAuth = false;
                await digestAuth.fetchAuth(requestUrl, options);
                if (digestAuth.hasAuth) {
                    this.lastAuthenticated = new Date();
                }
            } finally {
                this.refreshPromise = null;
            }
        })();

        await this.refreshPromise;
    }

    async sendAuthenticated(request: HttpRequest<any>, next: HttpHandler, options: any, allowRetry = true) {
        const digestAuth = this.authService.userDigestAuth;
        const _options = digestAuth.addAuth(request.url, options);
        if (!_options || !_options.headers || !_options.headers.Authorization) {
            return null;
        }
        const req = request.clone({
            setHeaders: {
                Authorization: _options.headers.Authorization,
                ngBrowserVersion: 'true'
            }
        });
        try {
            return await next.handle(req).toPromise();
        } catch (error) {
            if (this.isAuthChallenge(error)) {
                console.log('401 Unauthorized error occurred - preventing browser popup');
                if (allowRetry) {
                    const www = this.getWwwAuthenticate(error);
                    if (www) {
                        await digestAuth.parseAuth(www);
                    } else {
                        digestAuth.hasAuth = false;
                        await digestAuth.fetchAuth(request.url, options);
                    }
                    if (digestAuth.hasAuth) {
                        this.lastAuthenticated = new Date();
                    }
                    return this.sendAuthenticated(request, next, options, false);
                }
            }
            throw error;
        }
    }

    async getHeader(request: HttpRequest<any>, next: HttpHandler) {

        const headers = { "ngBrowserVersion": "true" };
        const factory = () => ({ method: request.method, headers });
        const options: any = { factory };

        await this.refreshDigestAuth(request.url, options);

        if (this.authService.userDigestAuth) {
            const result = await this.sendAuthenticated(request, next, options);
            if (result) {
                return result;
            }
        }

        const req = request.clone({
            setHeaders: {
                ngBrowserVersion: 'true',
            }
        });

        return next.handle(req).toPromise().catch(error => {
            if (error.status === 401) {
                console.log('401 Unauthorized error occurred - preventing browser popup');
            }
            throw error;
        });
    }
}
